import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { Business, Description, Groups, Person } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetChecklistDocumentsQuery } from "@api/configuration";
import { useLazyGetGuaranteeTypeQuery } from "@api/catalogs";
import { filterChecklistByGuarantee } from "@components/CustomCheckList/functions";
import { ChecklistDocuments, ChecklistIcon, GuaranteeType } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { DEFAULT_CHECKLIST_PM } from "@helpers/defaultConfig";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

const CHECKLIST_ICONS: Record<ChecklistIcon, ReactNode> = {
  building: <Business fontSize="small" />,
  user: <Person fontSize="small" />,
  users: <Groups fontSize="small" />,
  document: <Description fontSize="small" />,
};

interface DocSectionProps {
  title: string;
  items: string[];
  icon?: ChecklistIcon;
}

const DocSection = ({ title, items, icon }: DocSectionProps) => (
  <Paper variant="lendia_paper" sx={{ p: "20px", display: "flex", flexDirection: "column", height: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "14px" }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "6px",
          bgcolor: "#F4F6F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        {(icon && CHECKLIST_ICONS[icon]) || CHECKLIST_ICONS.document}
      </Box>
      <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark" }}>
        {title}
      </Typography>
    </Box>

    <Box
      component="ul"
      sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}
    >
      {items.map((it) => (
        <Box component="li" key={it} sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "text.disabled", flexShrink: 0, mt: "8px" }} />
          <Typography component="span" sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.45 }}>
            {it}
          </Typography>
        </Box>
      ))}
    </Box>
  </Paper>
);

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
  gridAutoRows: "1fr",
  gap: 2,
} as const;

export const ListaRevision = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.check_list.code);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getChecklistDocuments] = useLazyGetChecklistDocumentsQuery();
  const [getGuaranteeType] = useLazyGetGuaranteeTypeQuery();

  const [checklist, setChecklist] = useState<ChecklistDocuments>(DEFAULT_CHECKLIST_PM);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const cargarChecklist = useCallback(async () => {
    if (!application.tax_system_id) return;

    try {
      setLoadingChecklist(true);

      const [guarantees, documentos] = await Promise.all([
        getGuaranteeType().unwrap(),
        getChecklistDocuments({ tax_system_id: application.tax_system_id }).unwrap(),
      ]);

      if (documentos && documentos.length) {
        const guaranteeType: GuaranteeType | undefined = application.guarantee_type_id
          ? guarantees.find((item) => item.id === application.guarantee_type_id)
          : undefined;

        const filtrado = filterChecklistByGuarantee(documentos, guaranteeType);
        filtrado.sort((a, b) => (a.order || 0) - (b.order || 0));
        setChecklist(filtrado);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ListaRevision-checklist_fetch" } });
    } finally {
      setLoadingChecklist(false);
    }
  }, [application.tax_system_id, application.guarantee_type_id, getGuaranteeType, getChecklistDocuments]);

  useEffect(() => {
    cargarChecklist();
  }, [cargarChecklist]);

  const onNext = async () => {
    try {
      setIsLoading(true);

      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(nextStatus.url || "");
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ListaRevision-continuar" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente digital"
        title="Reúne tu expediente"
        subtitle="Para procesar tu crédito necesitamos algunos documentos y datos. Puedes subirlos en cualquier orden y en varias sesiones."
      />

      <Box sx={GRID_SX}>
        {loadingChecklist
          ? [0, 1, 2, 3].map((index) => (
              <Skeleton key={index} variant="rectangular" animation="wave" sx={{ height: 210, borderRadius: "14px" }} />
            ))
          : checklist.map((item) => (
              <DocSection key={item.title} title={item.title} items={item.documents} icon={item.icon} />
            ))}
      </Box>

      <StepActions
        onNext={onNext}
        nextLabel="Comenzar expediente"
        nextDisabled={isLoading || loadingChecklist}
        nextTestId="lista-revision-continue-button"
      />
    </Box>
  );
};
